import React, { useState, useEffect, useCallback } from "react";
import SideBar from "../components/SideBar";
import "../styles/ClosingAndSettlingAccounts.css";
import { accountClosureService } from "../services/accountClosureService";
import { toast } from "react-toastify";
import {
  MdOutlineAccountBalanceWallet,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlinePictureAsPdf,
  MdOutlineRefresh,
  MdOutlineSearch,
  MdOutlineLock,
  MdOutlineArrowForward,
  MdOutlineHourglassEmpty,
  MdOutlineDoneAll,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";

function ClosingAndSettlingAccounts() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    settled: 0,
    rejected: 0,
    total_settled_topcoin: 0,
    total_settled_amount: 0,
  });

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Selected request modal & details
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [liveBalances, setLiveBalances] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Settlement Form State
  const [settlementAmount, setSettlementAmount] = useState("");
  const [settlementMethod, setSettlementMethod] = useState("Bank Transfer");
  const [settlementRef, setSettlementRef] = useState("");
  const [settlementNotes, setSettlementNotes] = useState("");
  const [isSubmittingSettlement, setIsSubmittingSettlement] = useState(false);

  // Rejection State
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);

  // Finalisation State
  const [isFinalising, setIsFinalising] = useState(false);

  // Fetch Requests
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountClosureService.getClosureRequests({
        page: currentPage,
        status: statusFilter,
        user_type: userTypeFilter,
        search: search.trim(),
      });

      if (res.success) {
        setRequests(res.data.data || []);
        setCounts(res.counts || {});
        setCurrentPage(res.data.current_page || 1);
        setTotalPages(res.data.last_page || 1);
        setTotalRecords(res.data.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch account closure requests:", error);
      toast.error(error.message || "Failed to load account closure requests.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, userTypeFilter, search]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Handle open request detail
  const handleOpenDetails = async (reqId) => {
    setIsModalOpen(true);
    setDetailLoading(true);
    setShowRejectForm(false);
    setRejectionReason("");

    try {
      const res = await accountClosureService.getClosureRequestDetails(reqId);
      if (res.success) {
        const item = res.request;
        setSelectedRequest(item);
        setLiveBalances(res.live_balances);

        // Prepopulate settlement amount from balance or existing settlement
        if (item.settlement_amount !== null && item.settlement_amount !== undefined) {
          setSettlementAmount(item.settlement_amount);
        } else {
          // Default estimation or 0
          setSettlementAmount(
            res.live_balances?.total_topcoin_balance ?? item.total_topcoin_balance ?? 0
          );
        }

        setSettlementMethod(item.settlement_method || "Bank Transfer");
        setSettlementRef(
          item.settlement_reference ||
            `TCUK-SETTLE-${String(item.id).padStart(6, "0")}`
        );
        setSettlementNotes(item.settlement_notes || "");
      }
    } catch (error) {
      console.error("Failed to load details:", error);
      toast.error("Failed to load request details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
    setLiveBalances(null);
  };

  // Approve Request
  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      const res = await accountClosureService.approveRequest(selectedRequest.id);
      if (res.success) {
        toast.success(res.message || "Request approved successfully!");
        setSelectedRequest(res.request);
        fetchRequests();
      }
    } catch (error) {
      toast.error(error.message || "Failed to approve request.");
    }
  };

  // Reject Request
  const handleReject = async () => {
    if (!selectedRequest) return;
    if (!rejectionReason.trim()) {
      toast.error("Please enter a rejection reason.");
      return;
    }

    setIsSubmittingReject(true);
    try {
      const res = await accountClosureService.rejectRequest(selectedRequest.id, {
        rejection_reason: rejectionReason,
      });
      if (res.success) {
        toast.success(res.message || "Request rejected.");
        setSelectedRequest(res.request);
        setShowRejectForm(false);
        fetchRequests();
      }
    } catch (error) {
      toast.error(error.message || "Failed to reject request.");
    } finally {
      setIsSubmittingReject(false);
    }
  };

  // Settle Request
  const handleSettle = async (e) => {
    e.preventDefault();
    if (!selectedRequest) return;

    if (settlementAmount === "" || isNaN(settlementAmount)) {
      toast.error("Please enter a valid settlement amount.");
      return;
    }

    setIsSubmittingSettlement(true);
    try {
      const res = await accountClosureService.settleRequest(selectedRequest.id, {
        settlement_amount: parseFloat(settlementAmount),
        settlement_currency: "GBP",
        settlement_method: settlementMethod,
        settlement_reference: settlementRef,
        settlement_notes: settlementNotes,
      });

      if (res.success) {
        toast.success(res.message || "Settlement completed and PDF statement generated!");
        setSelectedRequest(res.request);
        fetchRequests();
      }
    } catch (error) {
      toast.error(error.message || "Failed to settle request.");
    } finally {
      setIsSubmittingSettlement(false);
    }
  };

  // Finalise Closure
  const handleFinalise = async () => {
    if (!selectedRequest) return;

    const confirmed = window.confirm(
      "CONFIRM PERMANENT ACCOUNT CLOSURE:\n\n" +
        "Are you sure you want to finalise this account closure?\n" +
        "1. Account status will be permanently set to 'PermanentClosure'.\n" +
        "2. The user will be blocked from accessing any portal.\n" +
        "3. This email cannot be used to register or join again in the future forever."
    );

    if (!confirmed) return;

    setIsFinalising(true);
    try {
      const res = await accountClosureService.finaliseRequest(selectedRequest.id);
      if (res.success) {
        toast.success("Account closure finalised! Status set to PermanentClosure.");
        setSelectedRequest(res.request);
        fetchRequests();
      }
    } catch (error) {
      toast.error(error.message || "Failed to finalise closure.");
    } finally {
      setIsFinalising(false);
    }
  };

  // Download Statement PDF
  const handleDownloadPdf = async (reqId) => {
    try {
      toast.info("Preparing PDF download...");
      await accountClosureService.downloadStatementPdf(
        reqId,
        `TopCoin_Final_Statement_${reqId}.pdf`
      );
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download statement PDF.");
    }
  };

  // Render pagination number buttons with windowing
  const renderPaginationNumbers = () => {
    if (totalPages <= 1) {
      return (
        <button className="closing-page-num active" disabled>
          1
        </button>
      );
    }

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          className={`closing-page-num ${currentPage === 1 ? "active" : ""}`}
          onClick={() => setCurrentPage(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="dots-start" className="closing-page-dots">
            …
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`closing-page-num ${currentPage === i ? "active" : ""}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="dots-end" className="closing-page-dots">
            …
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          className={`closing-page-num ${currentPage === totalPages ? "active" : ""}`}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <div className="closing-accounts-layout">
      <SideBar />
      <div className="closing-accounts-main">
        {/* Header Section */}
        <div className="closing-header-bar">
          <div className="closing-header-top">
            <div>
              <h1 className="closing-header-title">
                Closing Accounts &amp; Settling Accounts
              </h1>
              {/* <div className="closing-header-sub">
                Review closure requests, calculate final TopCoin balances, settle outstanding balances, generate statements, and finalise PermanentClosure status.
              </div> */}
            </div>
            {/* <button
              className="closing-btn closing-btn-secondary"
              onClick={fetchRequests}
              disabled={loading}
            >
              <MdOutlineRefresh size={18} /> Refresh
            </button> */}
          </div>
        </div>

        {/* Summary Statistics Cards */}
        {/* <div className="closing-stats-grid">
          <div className="closing-stat-card">
            <span className="closing-stat-label">Total Requests</span>
            <span className="closing-stat-value">{counts.total || 0}</span>
            <span className="closing-stat-sub">Submitted closure requests</span>
          </div>
          <div className="closing-stat-card" style={{ borderLeft: "4px solid #f59e0b" }}>
            <span className="closing-stat-label">Pending Review</span>
            <span className="closing-stat-value" style={{ color: "#d97706" }}>
              {counts.pending || 0}
            </span>
            <span className="closing-stat-sub">Awaiting admin review</span>
          </div>
          <div className="closing-stat-card" style={{ borderLeft: "4px solid #0284c7" }}>
            <span className="closing-stat-label">In Settlement / Approved</span>
            <span className="closing-stat-value" style={{ color: "#0284c7" }}>
              {counts.approved || 0}
            </span>
            <span className="closing-stat-sub">Approved for settlement</span>
          </div>
          <div className="closing-stat-card" style={{ borderLeft: "4px solid #16a34a" }}>
            <span className="closing-stat-label">Settled &amp; PermanentClosure</span>
            <span className="closing-stat-value" style={{ color: "#16a34a" }}>
              {counts.settled || 0}
            </span>
            <span className="closing-stat-sub">Finalised permanent closures</span>
          </div>
          <div className="closing-stat-card" style={{ borderLeft: "4px solid #0000a0" }}>
            <span className="closing-stat-label">Total Settled TopCoins</span>
            <span className="closing-stat-value" style={{ color: "#0000a0" }}>
              {Number(counts.total_settled_topcoin || 0).toLocaleString()} TC
            </span>
            <span className="closing-stat-sub">
              Settled: £{Number(counts.total_settled_amount || 0).toFixed(2)}
            </span>
          </div>
        </div> */}

        {/* Filter and Search Bar */}
        <div className="closing-filters-card">
          <div className="closing-filter-group">
            <input
              type="text"
              className="closing-search-input"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="closing-filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="settled">Settled (PermanentClosure)</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              className="closing-filter-select"
              value={userTypeFilter}
              onChange={(e) => {
                setUserTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Account Types</option>
              <option value="Customer">Customer</option>
              <option value="Customer Ads Agent">Customer Ads Agent</option>
              <option value="Business Ads Agent">Business Ads Agent</option>
              {/* <option value="Retail Business">Retail Business</option> */}
              {/* <option value="Member">Member</option> */}
            </select>
          </div>
          <div>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              Showing {requests.length} of {totalRecords} records
            </span>
          </div>
        </div>

        {/* Main Records Table */}
        <div className="closing-table-container">
          <table className="closing-table">
            <thead>
              <tr>
                <th>Ref ID</th>
                <th>Account Holder</th>
                <th>Account Type</th>
                <th>Date Requested</th>
                <th>Final TopCoins</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                    Loading account closure requests...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    No account closure requests found.
                  </td>
                </tr>
              ) : (
                requests.map((req) => (
                  <tr key={req.id}>
                    <td>
                      <strong>TCUK-CLS-{String(req.id).padStart(6, "0")}</strong>
                    </td>
                    <td>
                      <div>
                        <strong>
                          {req.first_name} {req.last_name}
                        </strong>
                      </div>
                      <div style={{ fontSize: "12px", color: "#64748b" }}>
                        {req.email}
                      </div>
                    </td>
                    <td>
                      <span className="role-badge">{req.user_type}</span>
                    </td>
                    <td>
                      {req.created_at
                        ? new Date(req.created_at).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "N/A"}
                    </td>
                    <td>
                      <strong style={{ color: "#0000a0" }}>
                        {Number(req.total_topcoin_balance || 0).toLocaleString()} TC
                      </strong>
                    </td>
                    <td>
                      <span className={`status-pill ${req.status}`}>
                        {req.status === "settled" ? "PermanentClosure" : req.status}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: "8px" }}>
                        <button
                          className="closing-btn closing-btn-primary"
                          onClick={() => handleOpenDetails(req.id)}
                        >
                          Review &amp; Manage
                        </button>
                        {req.status === "settled" && (
                          <button
                            className="closing-btn closing-btn-secondary"
                            title="Download Statement PDF"
                            onClick={() => handleDownloadPdf(req.id)}
                          >
                            <MdOutlinePictureAsPdf size={16} /> PDF
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div className="closing-pagination ">
            {/* <div className="closing-pagination-info">
              Showing{" "}
              <strong>{totalRecords === 0 ? 0 : (currentPage - 1) * 15 + 1}</strong>{" "}
              to{" "}
              <strong>{Math.min(currentPage * 15, totalRecords)}</strong>{" "}
              of <strong>{totalRecords}</strong> records
              <span className="closing-pagination-badge">
                Page {currentPage} of {Math.max(1, totalPages)}
              </span>
            </div> */}

            <div className="closing-pagination-nav">
              <button
                className="closing-page-nav-btn"
                disabled={currentPage <= 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <MdChevronLeft size={18} />
                <span>Previous</span>
              </button>

              <div className="closing-pagination-pages">
                {renderPaginationNumbers()}
              </div>

              <button
                className="closing-page-nav-btn"
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                <span>Next</span>
                <MdChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Review, Settlement & Finalisation Modal */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content-card">
              <div className="modal-header">
                <div>
                  <h2 className="modal-title">
                    Account Closure &amp; Settlement Management
                  </h2>
                  <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
                    Reference: TCUK-CLS-{selectedRequest ? String(selectedRequest.id).padStart(6, "0") : ""}
                  </div>
                </div>
                <button className="modal-close-icon" onClick={handleCloseModal}>
                  &times;
                </button>
              </div>

              {detailLoading || !selectedRequest ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  Loading details...
                </div>
              ) : (
                <div>
                  {/* Status Banner */}
                  {selectedRequest.status === "settled" && (
                    <div className="alert-permanent-closure">
                      <p>
                        <strong>PERMANENT CLOSURE RECORD:</strong> This account closure has been finalized. Actual account status is set to <strong>PermanentClosure</strong>. The user cannot access the portal or register again in the future with this email address.
                      </p>
                    </div>
                  )}

                  {selectedRequest.status === "rejected" && (
                    <div className="section-box" style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
                      <div className="section-box-title" style={{ color: "#991b1b" }}>
                        <span>Account Closure Request Rejected</span>
                        {/* <span className="status-pill approved" style={{ background: "#dcfce7", color: "#166534" }}>
                          Status: Active (Approved)
                        </span> */}
                      </div>
                      {/* <p style={{ fontSize: "13px", color: "#7f1d1d", margin: "0 0 8px 0" }}>
                        This closure request was rejected. The account status has been automatically reactivated to <strong>Approved</strong>.
                      </p> */}
                      <div style={{ fontSize: "13px", color: "#991b1b" }}>
                        <strong>Rejection Reason:</strong> {selectedRequest.rejection_reason || "Not specified"}
                      </div>
                    </div>
                  )}

                  {/* Account Particulars */}
                  <div className="section-box">
                    <div className="section-box-title">
                      <span>Account Holder Details</span>
                      <span className={`status-pill ${selectedRequest.status}`}>
                        {selectedRequest.status === "settled"
                          ? "PermanentClosure"
                          : selectedRequest.status}
                      </span>
                    </div>
                    <div className="details-row">
                      <div className="details-col">
                        <div className="details-label">Full Name</div>
                        <div className="details-value">
                          {selectedRequest.first_name} {selectedRequest.last_name}
                        </div>
                      </div>
                      <div className="details-col">
                        <div className="details-label">Email Address</div>
                        <div className="details-value">
                          {selectedRequest.email}
                        </div>
                      </div>
                      <div className="details-col">
                        <div className="details-label">Account Role</div>
                        <div className="details-value">
                          {selectedRequest.user_type}
                        </div>
                      </div>
                    </div>
                    <div className="details-row">
                      <div className="details-col">
                        <div className="details-label">Phone Number</div>
                        <div className="details-value">
                          {selectedRequest.phone_number || "N/A"}
                        </div>
                      </div>
                      <div className="details-col">
                        <div className="details-label">Date Submitted</div>
                        <div className="details-value">
                          {new Date(selectedRequest.created_at).toLocaleString("en-GB")}
                        </div>
                      </div>
                      <div className="details-col">
                        <div className="details-label">Closure Reason</div>
                        <div className="details-value">
                          {selectedRequest.reason || "Not specified"}
                          {selectedRequest.other_reason ? ` (${selectedRequest.other_reason})` : ""}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Calculated TopCoin Wallet Balances */}
                  <div className="section-box">
                    <div className="section-box-title">
                      <span>Final Calculated TopCoin Balance</span>
                      {/* <span style={{ fontSize: "12px", color: "#64748b", textTransform: "none" }}>
                        Live Wallet Calculation
                      </span> */}
                    </div>
                    <div className="balance-grid">
                      <div className="balance-card">
                        <div className="balance-card-label">Primary Wallet</div>
                        <div className="balance-card-value">
                          {Number(
                            liveBalances
                              ? liveBalances.wallet_balance
                              : selectedRequest.wallet_balance
                          ).toLocaleString()}{" "}
                          TC
                        </div>
                      </div>
                      <div className="balance-card">
                        <div className="balance-card-label">Bonus</div>
                        <div className="balance-card-value">
                          {Number(
                            liveBalances
                              ? liveBalances.secondary_wallet_balance
                              : selectedRequest.secondary_wallet_balance
                          ).toLocaleString()}{" "}
                          TC
                        </div>
                        {/* {Number(liveBalances?.bonus_balance || 0) > 0 && (
                          <div style={{ fontSize: "11px", color: "#166534", marginTop: "3px", fontWeight: "600" }}>
                            (Incl. {Number(liveBalances.bonus_balance).toLocaleString()} Bonus TC)
                          </div>
                        )} */}
                      </div>
                      {/* <div className="balance-card">
                        <div className="balance-card-label">Agent Network</div>
                        <div className="balance-card-value">
                          {Number(
                            liveBalances
                              ? liveBalances.agent_wallet_balance
                              : selectedRequest.agent_wallet_balance
                          ).toLocaleString()}{" "}
                          TC
                        </div>
                      </div> */}
                      <div className="balance-card highlight">
                        <div className="balance-card-label">Total Final TopCoins</div>
                        <div className="balance-card-value">
                          {Number(
                            liveBalances
                              ? liveBalances.total_topcoin_balance
                              : selectedRequest.total_topcoin_balance
                          ).toLocaleString()}{" "}
                          TC
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lifecycle Actions */}
                  {selectedRequest.status === "pending" && !showRejectForm && (
                    <div className="section-box" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
                      <div className="section-box-title" style={{ color: "#92400e" }}>
                        <span>Admin Review &amp; Approval</span>
                      </div>
                      <p style={{ fontSize: "13px", color: "#78350f", margin: "0 0 14px 0" }}>
                        Review the submitted request and calculated wallet balance. Approve the request to initiate the settlement process, or reject if necessary.
                      </p>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <button
                          className="closing-btn closing-btn-primary"
                          onClick={handleApprove}
                        >
                          <MdOutlineCheckCircle size={18} /> Approve Request for Settlement
                        </button>
                        <button
                          className="closing-btn closing-btn-danger"
                          onClick={() => setShowRejectForm(true)}
                        >
                          <MdOutlineCancel size={18} /> Reject Request
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Rejection Form */}
                  {showRejectForm && (
                    <div className="section-box" style={{ background: "#fef2f2", borderColor: "#fecaca" }}>
                      <div className="section-box-title" style={{ color: "#991b1b" }}>
                        Reject Closure Request
                      </div>
                      <div className="form-group">
                        <label className="form-label">Reason for Rejection</label>
                        <textarea
                          className="form-textarea"
                          rows="3"
                          placeholder="Provide detailed explanation for rejecting the closure request..."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                        />
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <button
                          className="closing-btn closing-btn-danger"
                          onClick={handleReject}
                          disabled={isSubmittingReject}
                        >
                          {isSubmittingReject ? "Rejecting..." : "Confirm Rejection"}
                        </button>
                        <button
                          className="closing-btn closing-btn-secondary"
                          onClick={() => setShowRejectForm(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Settlement Form & Details (Approved or Settled state) */}
                  {(selectedRequest.status === "approved" || selectedRequest.status === "settled") && (
                    <div className="section-box">
                      <div className="section-box-title">
                        <span>
                          {selectedRequest.status === "settled"
                            ? "Settlement & Statement Record"
                            : "Proceed with Settlement & Statement Generation"}
                        </span>
                        {selectedRequest.status === "settled" && (
                          <span className="status-pill settled">Settled</span>
                        )}
                      </div>

                      {selectedRequest.status === "settled" ? (
                        <div>
                          <div className="details-row">
                            <div className="details-col">
                              <div className="details-label">Settled Amount</div>
                              <div className="details-value" style={{ color: "#0000a0", fontWeight: "bold" }}>
                                {selectedRequest.settlement_currency || "GBP"} £
                                {Number(selectedRequest.settlement_amount*0.005 || 0).toFixed(2)}
                              </div>
                            </div>
                            <div className="details-col">
                              <div className="details-label">Payment Method</div>
                              <div className="details-value">
                                {selectedRequest.settlement_method || "N/A"}
                              </div>
                            </div>
                            <div className="details-col">
                              <div className="details-label">Payment Reference</div>
                              <div className="details-value">
                                <code>{selectedRequest.settlement_reference || "N/A"}</code>
                              </div>
                            </div>
                          </div>
                          <div className="details-row" style={{ marginTop: "12px" }}>
                            <div className="details-col">
                              <div className="details-label">Settled On</div>
                              <div className="details-value">
                                {selectedRequest.settled_at
                                  ? new Date(selectedRequest.settled_at).toLocaleString("en-GB")
                                  : "N/A"}
                              </div>
                            </div>
                            <div className="details-col">
                              <div className="details-label">Notes</div>
                              <div className="details-value">
                                {selectedRequest.settlement_notes || "None"}
                              </div>
                            </div>
                          </div>

                          {/* PDF Download Button */}
                          <div style={{ marginTop: "20px", display: "flex", gap: "12px" }}>
                            <button
                              className="closing-btn closing-btn-primary"
                              onClick={() => handleDownloadPdf(selectedRequest.id)}
                            >
                              <MdOutlinePictureAsPdf size={18} /> Download Final Account Statement (PDF)
                            </button>
                          </div>
                        </div>
                      ) : (
                        <form onSubmit={handleSettle}>
                          <p style={{ fontSize: "13px", color: "#64748b", marginTop: 0 }}>
                            Complete the settlement of any outstanding balance and generate the official PDF statement.
                          </p>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                            <div className="form-group">
                              <label className="form-label">Settlement Amount (£ GBP)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-input"
                                value={settlementAmount*0.005}
                                onChange={(e) => setSettlementAmount(e.target.value)}
                                placeholder="0.00"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Settlement Method</label>
                              <select
                                className="form-select"
                                value={settlementMethod}
                                onChange={(e) => setSettlementMethod(e.target.value)}
                              >
                                <option value="Bank Transfer">Bank Transfer (BACS)</option>
                                <option value="Cheque">Cheque Issued</option>
                                <option value="PayPal">PayPal</option>
                                <option value="Cash">Cash</option>
                                <option value="Crypto">Crypto</option>
                                <option value="Zero Balance Settled">Zero Balance / No Payout Required</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="form-label">Settlement Payment Reference / Transaction ID</label>
                            <input
                              type="text"
                              className="form-input"
                              value={settlementRef}
                              onChange={(e) => setSettlementRef(e.target.value)}
                              placeholder="e.g. BACS-REF-12345"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Settlement Notes (Optional)</label>
                            <textarea
                              className="form-textarea"
                              rows="2"
                              value={settlementNotes}
                              onChange={(e) => setSettlementNotes(e.target.value)}
                              placeholder="Add any audit or settlement notes..."
                            />
                          </div>

                          <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
                            <button
                              type="submit"
                              className="closing-btn closing-btn-primary"
                              disabled={isSubmittingSettlement}
                            >
                              <MdOutlinePictureAsPdf size={18} />
                              {isSubmittingSettlement
                                ? "Processing Settlement..."
                                : "Complete Settlement & Generate Statement PDF"}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}

                  {/* Step 3: Finalise Account Closure (PermanentClosure) */}
                  {selectedRequest.status === "approved" && selectedRequest.settled_at && (
                    <div className="section-box" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
                      <div className="section-box-title" style={{ color: "#166534" }}>
                        <span>Step 3: Finalise Account Closure</span>
                      </div>
                      <p style={{ fontSize: "13px", color: "#166534", margin: "0 0 14px 0" }}>
                        Settlement is completed and statement is ready. Click below to finalise the closure. This sets the actual account status to <strong>PermanentClosure</strong>. The user will be permanently blocked from future access and future re-joining with this email.
                      </p>
                      <button
                        className="closing-btn closing-btn-success"
                        onClick={handleFinalise}
                        disabled={isFinalising}
                      >
                        <MdOutlineLock size={18} />
                        {isFinalising
                          ? "Finalising Closure..."
                          : "Finalise Account Closure (Set PermanentClosure)"}
                      </button>
                    </div>
                  )}

                  {/* Modal Footer Actions */}
                  <div className="modal-actions">
                    <button
                      className="closing-btn closing-btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Close Window
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClosingAndSettlingAccounts;
